import React, { useState, useEffect } from 'react';
import { 
  BarChart3, Calendar, Clock, AlertTriangle, CheckCircle, 
  Package, ShoppingCart, TrendingUp, Filter, Search, 
  ChevronDown, ChevronRight, Edit, Trash2, Plus, 
  Download, Printer, RefreshCw, ArrowRight, FileText, ArrowLeft
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

const MRPDashboard = ({ setActiveView, handleViewPlan, handleViewItem, navigationSource = null, onReturnToWorkflows = null }) => {
  const [mrpData, setMrpData] = useState({
    plans: [],
    items: [],
    suggestions: []
  });
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch MRP data from Supabase
  useEffect(() => {
    fetchMrpData();
  }, []);

  const fetchMrpData = async () => {
    try {
      setLoading(true);
      
      // Fetch MRP plans
      const { data: plansData, error: plansError } = await supabase
        .from('mrp_plans')
        .select('*');

      if (plansError) throw plansError;

      // Fetch MRP items
      const { data: itemsData, error: itemsError } = await supabase
        .from('mrp_items')
        .select('*');

      if (itemsError) throw itemsError;

      // Fetch purchase suggestions
      const { data: suggestionsData, error: suggestionsError } = await supabase
        .from('purchase_suggestions')
        .select('*');

      if (suggestionsError) throw suggestionsError;

      // Fetch products
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('*');

      if (productsError) throw productsError;

      // Fetch suppliers
      const { data: suppliersData, error: suppliersError } = await supabase
        .from('suppliers')
        .select('*');

      if (suppliersError) throw suppliersError;

      setMrpData({
        plans: plansData || [],
        items: itemsData || [],
        suggestions: suggestionsData || []
      });
      setProducts(productsData || []);
      setSuppliers(suppliersData || []);
    } catch (error) {
      console.error('Error fetching MRP data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const getStatusDetails = (status, type = 'plan') => {
    const statusMap = {
      'active': { name: '執行中', color: '#10B981', icon: CheckCircle },
      'draft': { name: '草稿', color: '#6B7280', icon: FileText },
      'completed': { name: '已完成', color: '#059669', icon: CheckCircle },
      'critical': { name: '關鍵', color: '#EF4444', icon: AlertTriangle },
      'delayed': { name: '延遲', color: '#F59E0B', icon: Clock },
      'pending': { name: '待處理', color: '#3B82F6', icon: Clock }
    };
    
    return statusMap[status] || { name: '未知', color: '#6B7280', icon: FileText };
  };
  
  const getProductDetails = (productId) => {
    return products.find(p => p.id === productId) || { name: productId || '未知產品', product_code: productId || 'N/A' };
  };
  
  const getSupplierDetails = (supplierId) => {
    return suppliers.find(s => s.id === supplierId) || { name: '未知供應商' };
  };
  
  // Calculate MRP statistics
  const mrpStats = {
    activePlans: mrpData.plans.filter(p => p.status === 'active').length,
    criticalItems: mrpData.items.filter(i => i.priority === 'critical').length,
    delayedItems: mrpData.items.filter(i => i.status === 'delayed').length,
    pendingSuggestions: mrpData.suggestions.filter(s => s.status === 'pending').length
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading MRP dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h2 className="text-2xl font-bold text-gray-800">MRP規劃儀表板</h2>
          {navigationSource === 'workflows' && onReturnToWorkflows && (
            <button
              onClick={onReturnToWorkflows}
              className="flex items-center px-3 py-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              返回業務流程
            </button>
          )}
        </div>
        <div className="flex space-x-2">
          <button 
            onClick={() => setActiveView('plans')} 
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
          >
            <Calendar className="w-4 h-4 mr-2" />
            查看MRP計劃
          </button>
          <button 
            onClick={() => setActiveView('forecast')} 
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors flex items-center"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            需求預測
          </button>
        </div>
      </div>
      
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">執行中計劃</p>
              <p className="text-2xl font-bold text-gray-800">{mrpStats.activePlans}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">關鍵物料</p>
              <p className="text-2xl font-bold text-gray-800">{mrpStats.criticalItems}</p>
            </div>
            <div className="bg-red-100 p-3 rounded-full">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">延遲項目</p>
              <p className="text-2xl font-bold text-gray-800">{mrpStats.delayedItems}</p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">待處理採購建議</p>
              <p className="text-2xl font-bold text-gray-800">{mrpStats.pendingSuggestions}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <ShoppingCart className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Active MRP Plan */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-800">當前執行中的MRP計劃</h3>
          <img 
            src="https://images.unsplash.com/photo-1512758017271-d7b84c2113f1?w=800&h=600&fit=crop" 
            alt="MRP Planning" 
            className="h-10 w-auto rounded"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">計劃名稱</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">規劃期間</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">開始日期</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">結束日期</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">狀態</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {mrpData.plans.filter(plan => plan.status === 'active').map((plan) => {
                const status = getStatusDetails(plan.status);
                return (
                  <tr 
                    key={plan.id} 
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{plan.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{plan.planningPeriod}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{plan.startDate}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{plan.endDate}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span 
                        className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full" 
                        style={{ backgroundColor: `${status.color}20`, color: status.color }}
                      >
                        {status.name}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button 
                        onClick={() => handleViewPlan(plan)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        查看
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Critical Items */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-800">關鍵物料項目</h3>
          <img 
            src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&h=600&fit=crop" 
            alt="Critical Materials" 
            className="h-10 w-auto rounded"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">物料</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">需求數量</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">可用數量</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">淨需求</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">狀態</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {mrpData.items.filter(item => item.priority === 'critical').map((item) => {
                const status = getStatusDetails(item.status, 'item');
                const product = getProductDetails(item.productId);
                return (
                  <tr 
                    key={item.id} 
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{product.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.requiredQuantity}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.availableQuantity}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.netRequirement}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span 
                        className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full" 
                        style={{ backgroundColor: `${status.color}20`, color: status.color }}
                      >
                        {status.name}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button 
                        onClick={() => handleViewItem(item)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        查看
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Purchase Suggestions */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-800">採購建議</h3>
          <img 
            src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=600&fit=crop" 
            alt="Purchase Suggestions" 
            className="h-10 w-auto rounded"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">物料</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">建議數量</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">建議訂購日期</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">建議供應商</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">緊急程度</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {mrpData.suggestions.filter(s => s.status === 'pending').map((suggestion) => {
                const product = getProductDetails(suggestion.productId);
                const supplier = getSupplierDetails(suggestion.suggestedSupplierId);
                return (
                  <tr 
                    key={suggestion.id} 
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{product.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{suggestion.suggestedQuantity}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{suggestion.suggestedOrderDate}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{supplier.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span 
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${suggestion.urgency === 'high' ? 'bg-red-100 text-red-800' : suggestion.urgency === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}
                      >
                        {suggestion.urgency === 'high' ? '高' : suggestion.urgency === 'medium' ? '中' : '低'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button className="text-blue-600 hover:text-blue-900 mr-2">
                        核准
                      </button>
                      <button className="text-gray-600 hover:text-gray-900">
                        拒絕
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MRPDashboard;
